import { fromEvent } from 'rxjs';

import { Injectable, NgZone } from '@angular/core';
import { PlatformService } from '@core/services/platform.service';
import { ParallaxMember } from '@features/parallax/parallax';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class ParallaxService {
    private parallaxingElements: (ParallaxMember & { currentTranslate: number; offsetTop: number })[] = [];

    private lastScroll = 0;
    private scrollValues = {
        scrollY: 0,
        scrollUp: false
    };

    constructor(
        private ngZone: NgZone,
        private platformService: PlatformService
    ) {
        if (this.platformService.isServer) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            fromEvent(document, 'scroll')
                .pipe(untilDestroyed(this))
                .subscribe(() => {
                    this.scrollValues = {
                        scrollY: window.scrollY,
                        scrollUp: window.scrollY < this.lastScroll
                    };
                    this.lastScroll = window.scrollY;
                });
        });
    }

    public startParallax(member: ParallaxMember): void {
        const [, translateY] = this.getTranslate3d(member.element);
        const currentTranslate = translateY ? parseInt(translateY, 10) : 0;
        this.parallaxingElements.push({ ...member, offsetTop: window.scrollY, currentTranslate });

        this.doParallax();
    }

    public stopParallax(element: Element): void {
        for (const [index, member] of this.parallaxingElements.entries()) {
            if (member.element === element) {
                this.parallaxingElements.splice(index, 1);
            }
        }
    }

    private doParallax(): void {
        this.ngZone.runOutsideAngular(() => {
            for (const member of this.parallaxingElements) {
                const travelVelocity = (this.scrollValues.scrollY - member.offsetTop) / member.dullness;
                const travel = travelVelocity + member.currentTranslate;

                if (travel >= 0 && travel <= member.travel) {
                    member.element.style.transform = `translate3d(0, ${travel}px, 0)`;
                }
            }
            this.requestTick(this.doParallax.bind(this));
        });
    }

    private requestTick(callBack: FrameRequestCallback): void {
        if (this.parallaxingElements.length) {
            requestAnimationFrame(callBack);
        }
    }

    private getTranslate3d(el: HTMLElement): string[] {
        const values = el.style.transform.split(/\w+\(|\);?/);
        if (!values[1] || !values[1].length) {
            return [];
        }
        return values[1].split(/,\s?/g);
    }
}
